import audioManifest from '../../public/prototype/audio-manifest.json';
import {
  campuses,
  type Entity,
  type Kind,
  type Snapshot,
  seedUuid,
  teacherName,
  teachers,
} from './types';
export const initialTime = '2026-10-21T09:00:00+08:00';
export function createSeed(): Snapshot {
  let serial = 0;
  const entities: Entity[] = [];
  const add = (kind: Kind, title: string, extra: Partial<Entity> = {}) => {
    const item: Entity = {
      uuid: seedUuid(++serial),
      kind,
      title,
      campus: campuses[(serial - 1) % 3],
      memberUuid: teachers[(serial - 1) % 4],
      status: 'draft',
      createdAt: initialTime,
      updatedAt: initialTime,
      revision: 1,
      note: '',
      ...extra,
    };
    entities.push(item);
    return item;
  };
  for (let i = 0; i < 18; i++) {
    const device = add('device', `工牌 ${String(i + 1).padStart(3, '0')}`, {
      status: i < 3 ? 'stock' : 'assigned',
      campus: campuses[Math.floor(i / 6)],
      model: ['本地采集型', '远程回传型', '能力待验证型'][i % 3],
      supportsRemote: i % 3 === 1,
      battery: i === 4 ? 12 : 85,
      reportedAt: i === 5 ? undefined : initialTime,
    });
    if (i >= 3)
      add('binding', `${device.title}领用`, {
        status: 'active',
        deviceUuid: device.uuid,
        campus: device.campus,
        memberUuid: device.memberUuid,
        startAt: '2026-10-01T08:00:00+08:00',
      });
  }
  for (let i = 0; i < 12; i++)
    add('teacher', teacherName(teachers[i]), {
      status: 'active',
      memberUuid: teachers[i],
    });
  for (let i = 0; i < 18; i++)
    add('student', `学生 ${String(i + 1).padStart(3, '0')}`, {
      status: 'active',
    });
  for (let i = 0; i < 3; i++)
    add('station', `${campuses[i]}采集站`, {
      campus: campuses[i],
      status: 'active',
    });
  for (let i = 0; i < 3; i++)
    add('model', ['本地采集型', '远程回传型', '能力待验证型'][i], {
      status: i === 2 ? 'pending' : 'confirmed',
      fields: {
        录音读取: i === 2 ? '待验证' : '已验证',
        远程控制: i === 1 ? '已验证' : '不支持',
        定位: i === 1 ? '已验证' : '不支持',
      },
    });
  for (let i = 0; i < 60; i++) {
    const fixture = audioManifest[i % 3];
    const device = entities.filter((e) => e.kind === 'device')[i % 18];
    const uuid = seedUuid(10000 + i);
    add(
      'recording',
      `${['课堂提问与回应', '课后家长沟通', '教师指导复盘'][i % 3]} · ${i + 1}`,
      {
        uuid,
        status: 'ready',
        fixture: i % 3,
        campus: device.campus,
        memberUuid: device.memberUuid,
        deviceUuid: device.uuid,
        startAt: '2026-10-20T09:00:00+08:00',
        note: '虚构合成语音；台词为预置演示转写。',
        fields: { linkStatus: i % 8 === 0 ? 'pending' : 'confirmed' },
        versions: [
          {
            uuid: seedUuid(20000 + i),
            createdAt: initialTime,
            segments: fixture.segments.map((seg, j) => ({
              ...seg,
              uuid: seedUuid(30000 + i * 20 + j),
            })),
          },
        ],
      },
    );
  }
  for (const [kind, title] of [
    ['inventory', '工牌充电底座'],
    ['template', '沟通与教学观察'],
    ['rule', '关注承诺及后续跟进'],
    ['integration', '模拟教务同步'],
    ['help', '设备领用与采集指南'],
  ] as [Kind, string][])
    add(kind, title, {
      status: kind === 'inventory' ? 'active' : 'published',
      fields:
        kind === 'inventory'
          ? { 可用数量: '20' }
          : { 适用场景: '课堂与沟通', 版本: '1' },
    });
  const group = add('organization', '示例教育集团', {
    status: 'active',
    campus: campuses[0],
    fields: { type: '集团' },
  });
  const region = add('organization', '东部区域', {
    status: 'active',
    campus: campuses[0],
    fields: { type: '区域', parentUuid: group.uuid },
  });
  const west = add('organization', '西部区域', {
    status: 'active',
    campus: campuses[2],
    fields: { type: '区域', parentUuid: group.uuid },
  });
  campuses.forEach((campus, i) => {
    add('organization', campus, {
      status: 'active',
      campus,
      fields: { type: '校区', parentUuid: i < 2 ? region.uuid : west.uuid },
    });
  });
  const template = entities.find((e) => e.kind === 'template');
  if (template)
    template.fields = { scene: '课堂教学', output: '沟通线索', tested: 'true' };
  const rule = entities.find((e) => e.kind === 'rule');
  if (rule)
    rule.fields = {
      keyword: '周五',
      exclusion: '无需跟进',
      example: '周五前整理学习建议',
      tested: 'true',
    };
  add('modelConfig', '默认模拟处理配置', {
    status: 'published',
    campus: campuses[0],
    fields: { parallel: '2', timeoutMs: '10000' },
  });
  add('subscription', '原型演示服务', {
    status: 'published',
    campus: campuses[0],
    fields: {
      analysisLimit: '100',
      unitPrice: '10',
      expiresAt: '2027-10-21T09:00:00+08:00',
      release: '原型软件 1.0；无真实升级',
    },
  });
  for (let i = 0; i < 6; i++)
    add('lesson', `${['数学', '语文', '英语'][i % 3]}示例课次 ${i + 1}`, {
      status: 'draft',
      campus: campuses[i % 3],
      memberUuid: teachers[i % 4],
      fields: {
        subject: ['数学', '语文', '英语'][i % 3],
        goals: '练习表达思路并核实依据',
      },
    });
  const help = entities.find((e) => e.kind === 'help');
  if (help)
    help.fields = {
      category: '设备操作',
      body: '1. 在设备台账领用工牌。2. 模拟采集等待后台确认。3. 在录音中心回听。4. 分析建议需人工复核。所有数据均为虚构演示。',
    };
  for (const device of entities.filter(
    (e) => e.kind === 'device' && e.supportsRemote,
  )) {
    for (let i = 0; i < 3; i++)
      add('position', `${device.title}位置`, {
        status: 'ready',
        deviceUuid: device.uuid,
        campus: device.campus,
        createdAt: `2026-10-21T0${i + 6}:00:00+08:00`,
        fields: {
          x: String(0.2 + i * 0.1),
          y: String(0.3 + i * 0.1),
          accuracy: i === 1 ? '120' : '30',
          method: '模拟WiFi定位',
        },
      });
  }
  return {
    version: 1,
    revision: 1,
    now: initialTime,
    context: {
      role: 'campusManager',
      campus: campuses[0],
      viewMemberUuid: teachers[0],
    },
    entities,
    receipts: {},
    drafts: {},
  };
}
